<!-- eslint-disable vue/html-self-closing -->
<!-- eslint-disable max-len -->
<template>
  <section class="commit-diff-list">
    <div
      v-for="(file, i) in files"
      :key="hash(file)"
      class="d-flex justify-content-center"
    >
      <div
        class="commit-diff-list__file mb-3 w-100"
        :id="file.id"
        :name="file.id"
      >
        <div>
          <div
            class="commit-diff-list__file-header"
            :class="{ '--opened': !isFilesOpened[i] }"
          >
            <button
              class="commit-diff-list__open-button btn text-muted"
              type="button"
              @click="onOpenFileClick(i)"
            >
              <i
                class="gf-icon text-muted"
                :class="isFilesOpened[i] ? 'gf-icon-chevron-right' : 'gf-icon-chevron-down'"
                style="width: 15px"
              ></i>
            </button>
            <div class="commit-diff-list__file-header__info">
              <span style="padding-top: 2px">
                <i
                  class="gf-icon mr-1"
                  :class="{
                    [fileChangeTypeIcon(file.changeType || '')]: true,
                    'gf-icon-file text-warning': ['MODIFY', 'RENAME', 'COPY'].includes(
                      file.changeType
                    )
                  }"
                >
                </i>
              </span>
              <span class="commit-diff-list__file-header__info__file-path">
                <span v-if="!isFilesFullPathVisible[i]">
                  {{
                    getTruncatedFilePath(
                      file.changeType !== 'DELETE' ? file.newPath : file.oldPath,
                      i
                    )
                  }}
                </span>
                <span v-else>
                  {{ file.changeType !== 'DELETE' ? file.newPath : file.oldPath }}
                </span>
              </span>
              <gf-custom-clipboard
                :text-to-copy="file.changeType !== 'DELETE' ? file.newPath : file.oldPath"
                :show-only-button="true"
                button-class="p-1 mt-n1"
                style="width: initial"
              />

              <button
                v-if="isFullPath(i)"
                type="button"
                class="btn btn-sm text-muted p-0 mt-n1 mr-2"
                @click="toggleFullPathVisibility(i)"
              >
                <i
                  class="gf-icon"
                  :class="!isFilesFullPathVisible[i] ? 'gf-icon-eye' : 'gf-icon-eye-lock'"
                ></i>
              </button>
              <small
                v-if="file.newMode !== file.oldMode"
                class="commit-diff-list__file-header__info__file-modes"
              >
                {{ file.oldMode }}
                <i class="gf-icon gf-icon-arrow-right" />
                {{ file.newMode }}
              </small>

              <gf-custom-tooltip
                class="ml-2"
                v-if="file?.lockedBy"
              >
                <template #element>
                  <span class="d-flex align-items-center text-muted">
                    <i class="gf-icon gf-icon-lock"></i>
                  </span>
                </template>
                <template #content>
                  <span class="text-truncate">
                    {{ $t('file_locked_by') }}: @{{ file.lockedBy }}
                  </span>
                </template>
              </gf-custom-tooltip>
            </div>
            <div class="commit-diff-list__file-header__statistics">
              <gf-diff-statistics
                v-if="!file?.isBinary && !file?.isImg"
                class="mr-3"
                :added-lines-number="file?.addedLinesCount"
                :removed-lines-number="file?.removedLinesCount"
              ></gf-diff-statistics>
              <gf-dropdown
                v-if="hasMenu"
                position="right"
                btn-class="btn text-secondary py-0 px-1"
                btn-icon="gf-icon gf-icon-ellipsis-horizontal"
                :no-carret="true"
                style="--gfd-minw: 100px; display: none; float: right; text-align: left"
              >
                <a
                  :href="`/project/${sourceProject?.ownerAlias || alias}/${
                    sourceProject?.alias || projectAlias
                  }/blob?file=${file?.filePath}&${
                    sourceBranch?.title ? 'branch=' + sourceBranch?.title : 'commit=' + commitId
                  }`"
                  class="dropdown-item"
                >
                  {{ $t('message.file_show') }}
                </a>
              </gf-dropdown>
            </div>
          </div>
        </div>
        <div v-if="!isFilesOpened[i]">
          <div class="commit-diff-list__file-body">
            <div
              v-if="file.changeType === 'DELETE'"
              class="p-3 d-flex justify-content-center"
            >
              <span>Файл удален</span>
            </div>
            <div
              v-else-if="file.isCollapsed"
              class="p-3 d-flex justify-content-center"
            >
              <span>
                {{ $t('file_show_click') }}
                <a
                  href="#"
                  @click.prevent="[(file.isCollapsed = false), (file.ignoreCollapsed = true)]"
                >
                  {{ $t('message.here') }}
                </a>
                .
              </span>
            </div>
            <div
              v-else-if="file.isImg && file.isBinary"
              class="d-flex bg-light-second justify-content-center p-3"
            >
              <img
                class="mw-90 border border-blue"
                style="max-height: 400px; object-fit: contain"
                :src="sourceUrl(file)"
                alt="binary"
              />
            </div>
            <div
              v-else-if="file.isBinary"
              class="p-3 d-flex justify-content-center"
            >
              <span>
                <a :href="infoUrl(file)">{{ $tc('binary_file', 1) }}</a>
                ,
                {{ $tc('binary_file', 3) }}
              </span>
            </div>
            <div
              v-else-if="file.isLarge"
              class="p-3 d-flex justify-content-center"
            >
              <span>
                {{ $t('commit_diff_big') }}
                <a :href="infoUrl(file)">{{ $t('message.here') }}</a>
                .
              </span>
            </div>
            <div
              v-else-if="file.newMode === '0'"
              class="p-3 d-flex justify-content-center"
            >
              <span>
                {{ $t('message.file_deleted') }}
              </span>
            </div>
            <table
              v-else
              class="table table-sm mb-0 table-borderless"
            >
              <commit-diff-item
                :file="file"
                :commit-id="commitId"
                :alias="alias"
                :merge-request="mergeRequest"
                :project-alias="projectAlias"
                :has-discussions="hasDiscussions"
                :is-commit-diff-page="isCommitDiffPage"
                :source-commit="sourceCommit"
                :target-commit="targetCommit"
                @submitDiscussion="submitDiscussion($event, file)"
                @submitReply="submitReply($event, file)"
                @closeDiscussion="closeDiscussion"
              />
            </table>
          </div>
        </div>
      </div>
    </div>
    <empty-listing
      v-if="!!files && !files.length"
      :title="$tc('message.file', 3)"
    />
  </section>
</template>

<script>
import EmptyListing from '@shared/shared-components/empty-listing';
import objectHash from 'object-hash';
import MRService from '@shared/services/merge-request.service';
import helperS from '@shared/services/helper.service';
import { EventBus, EventEnums } from '@shared/event-bus';
import sharedComponentsMessages from '../sharedComponentsMessages';
// eslint-disable-next-line import/no-unresolved
import CommitDiffItem from './commit-diff-item';
import RepoS from '../../services/repository.service';
import '@shared/scss/code-highlight.scss';
import GfDiffStatistics from './GfDiffStatistics';
import GfCustomCollapse from '../shared/GfCustomCollapse.vue';
import GfDropdown from '@shared/shared-components/shared/GfDropdown';
import GfCustomTooltip from '../shared/GfCustomTooltip.vue';
import GfCustomClipboard from '@/shared-modules/shared-components/shared/GfCustomClipboard.vue';
import HelperS from '@/shared-modules/services/helper.service';

export default {
  name: 'CommitDiffList',
  components: {
    CommitDiffItem,
    EmptyListing,
    GfDiffStatistics,
    GfCustomCollapse,
    GfDropdown,
    GfCustomTooltip,
    GfCustomClipboard
  },
  props: {
    GfCustomTooltiplias: String,
    alias: String,
    projectAlias: String,
    commitId: String,
    fileList: Array,
    mergeRequest: {
      default: null,
      type: Object
    },
    hasDiscussions: {
      default: false,
      type: Boolean
    },
    isCommitDiffPage: {
      default: false,
      type: Boolean
    },
    sourceCommit: {
      default: null,
      type: String
    },
    targetCommit: {
      default: null,
      type: String
    },
    hasMenu: {
      type: Boolean,
      default: false
    },
    sourceBranch: {
      type: Object,
      default() {
        return {};
      }
    },
    sourceProject: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      maxShownFiles: 15,
      isFilesOpened: [],
      componentIsLoaded: false,
      isFilesFullPathVisible: []
    };
  },
  computed: {
    files() {
      return this.fileList || this.$store.getters.getBranchDiff;
    },
    infoUrl() {
      if (!this.isCommitDiffPage) {
        return (file) =>
          `/project/${this.alias}/${this.projectAlias}/blob?file=${file.newPath}&commit=${this.commitId}`;
      } else {
        return (file) =>
          `/project/${this.alias}/${this.projectAlias}/blob?file=${file.newPath}&commit=${this.targetCommit}`;
      }
    },
    sourceUrl() {
      return (file) =>
        `/project/${this.alias}/${this.projectAlias}/blob/raw?file=${file.newPath}&commit=${this.commitId}`;
    },
    selectedDiffItem() {
      return this.$route?.hash?.slice(1);
    }
  },
  watch: {
    files() {
      this.isFilesFullPathVisible = []
    }
  },
  mounted() {
    if (!this.fileList) {
      RepoS.getCommitDiff(this.alias, this.projectAlias, this.commitId).then(() => {
        if (this.selectedDiffItem) document.getElementById(this.selectedDiffItem).scrollIntoView();
      });
    }
    for (let i; i < this.files?.length; i++) {
      this.$set(this.isFilesOpened, i, true);
    }
  },
  updated() {
    if (this.selectedDiffItem && !this.componentIsLoaded)
      document.getElementById(this.selectedDiffItem).scrollIntoView();
    this.componentIsLoaded = true;
  },
  methods: {
    submitDiscussion(data) {
      MRService.createDiscussion(
        this.$route.params.alias,
        this.$route.params.projectAlias,
        helperS.generateDiscussionObject(data, this.mergeRequest)
      ).then(() => EventBus.$emit(EventEnums.MERGE_REQUEST_DISCUSSION_CREATED));
    },
    submitReply(data) {
      MRService.createDiscussion(
        this.$route.params.alias,
        this.$route.params.projectAlias,
        helperS.generateDiscussionObject(data, this.mergeRequest)
      ).then(() => EventBus.$emit(EventEnums.MERGE_REQUEST_DISCUSSION_REPLY_CREATED));
    },
    closeDiscussion(discussion) {
      MRService.resolveDiscussion(
        this.$route.params.alias,
        this.$route.params.projectAlias,
        this.$route.params.id,
        discussion
      );
    },
    hash(file) {
      return objectHash(file);
    },
    onOpenFileClick(fileIndex) {
      this.$set(this.isFilesOpened, fileIndex, !this.isFilesOpened[fileIndex]);
    },
    lockedByTheSameUser(file) {
      return file.lockedBy ? this.$store.getters.getUserAlias === file.lockedBy : true;
    },
    fileChangeTypeIcon(changeType) {
      switch (changeType) {
        case 'DELETE':
          return 'gf-icon-file-delete text-danger';
        case 'ADD':
          return 'gf-icon-file-new text-success';
        default:
          return 'gf-icon-file text-warning';
      }
    },
    getTruncatedFilePath(filePath, fileIndex) {
      const truncatedFilePath = HelperS.truncateContentFromCenter(filePath, {
        limitBase: 10,
        limitSm: 16,
        limitMd: 30,
        limitLg: 16,
        limitXl: 60,
        replacement: '...',
        separator: '/'
      });

      if (filePath !== truncatedFilePath) {
        this.isFilesFullPathVisible[fileIndex] = false;
      }

      return truncatedFilePath;
    },
    isFullPath(fileIndex) {      
      return this.isFilesFullPathVisible[fileIndex] !== undefined;
    },
    toggleFullPathVisibility(fileIndex) {
      this.$set(this.isFilesFullPathVisible, fileIndex, !this.isFilesFullPathVisible[fileIndex]);
    }
  },
  i18n: {
    messages: sharedComponentsMessages
  }
};
</script>

<style lang="scss">
$this: '.commit-diff-list';

#{$this}__file {
  scroll-margin-top: 60px;

  &-header {
    display: flex;
    align-items: flex-start;
    background-color: var(--light);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-lg);
    transition: 400ms 120ms cubic-bezier(0, 0, 0.3, 1);
    padding: 8px 16px;
    gap: 16px;

    &.--opened {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      transition: 220ms cubic-bezier(0, 0, 0.3, 1);
    }

    #{$this}__open-button {
      display: flex !important;
      align-items: flex-start !important;
      padding: 2px 4px;
    }

    #{$this}__open-button > i {
      padding-top: 0;
      width: 15px;
      font-size: 16px;
    }

    &__statistics {
      margin-left: auto;
      display: flex;
      align-items: flex-start;
    }

    &__info {
      display: flex !important;
      align-items: flex-start !important;
      width: 80%;

      &__file-path {
        margin-right: 4px;
        word-break: break-all;
      }

      &__file-modes {
        width: 25%;
        color: var(--text-muted);
      }
    }
  }

  &-body {
    padding: 0;
    word-break: break-word;
    border: 1px solid var(--border-color);
    border-top: none;
    border-bottom-left-radius: var(--border-radius-lg);
    border-bottom-right-radius: var(--border-radius-lg);
  }
}
</style>
